import { formatDate } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { LocaleService } from '@next/core-lib/i18n';
import { DateHelper } from 'src/app/shared/helpers/dateHelper';
import {
  IntervalType,
  ObservationsRegistration,
} from 'src/app/within-cycle-performance/models/within-cycle-performance.model';

import Utils from '../../shared/utils';
import { Barn } from '../models/barn-service.model';
import {
  BalancePerDeliveryLine,
  BalanceTableRow,
  BarnBalance,
  Cycle,
  DayRegistration,
  DeliveryOriginEnum,
  WeekRegistration,
} from '../models/cycle.model';

@Injectable()
export class CycleService {
  private readonly locale = inject<LocaleService>(LocaleService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  transformBalanceRows(
    balanceRows: BalancePerDeliveryLine[],
    cycle: Cycle,
  ): Array<BalanceTableRow> {
    const balance = new Array<BalanceTableRow>();

    const enum OrderType {
      Ascending,
      Descending,
    }

    /* eslint-disable */
    const fieldSorter =
      (fields: { name: string; order: OrderType }[]) =>
      (a: { [key: string]: any }, b: { [key: string]: any }): number =>
        /* eslint-enable */
        fields
          .map((field) => {
            if (field.order === OrderType.Ascending) {
              return <string>a[field.name] > <string>b[field.name]
                ? -1
                : <string>a[field.name] < <string>b[field.name]
                  ? 1
                  : 0;
            } else {
              return <string>a[field.name] > <string>b[field.name]
                ? 1
                : <string>a[field.name] < <string>b[field.name]
                  ? -1
                  : 0;
            }
          })
          .reduce((acc, cur) => acc || cur, 0);

    const processRow = (row: BalancePerDeliveryLine) => {
      let deliveryOrigin = row.deliverySource;
      if (row.balanceInOtherCycles.findIndex((d) => d.cycleNumber < cycle.cycleNumber) >= 0) {
        deliveryOrigin = DeliveryOriginEnum.START;
      }

      const previousConsumption = row.balanceInOtherCycles
        .filter((b) => b.cycleNumber < cycle.cycleNumber)
        .reduce((sum, b) => sum + b.amount, 0);
      const alreadyAllocated = row.barnBalances.reduce((sum, b) => sum + b.amount, 0);

      const quantity = row.baseQuantity - previousConsumption;
      const remainder = Math.max(quantity - alreadyAllocated, 0);

      balance.push(<BalanceTableRow>{
        balanceRowId: `${row.deliveryId}_${row.deliveryLineIdentifier}`,
        customerId: row.customerId,
        locationId: row.locationId,
        cycleId: row.cycleId,
        deliveryId: row.deliveryId,
        deliveryLineId: row.deliveryLineId,
        deliveryLineIdentifier: row.deliveryLineIdentifier,
        deliverySource: deliveryOrigin,
        type: deliveryOrigin,
        deliveryDate: row.deliveryDate,
        productIdentifier: row.productIdentifier,
        productTitle: row.productTitle,
        productType: row.productType,
        kgDelivered: row.kgDelivered,
        unitsDelivered: row.unitsDelivered,
        quantity,
        unitNettoPrice:
          row.unitNettoPrice != null ? parseFloat(row.unitNettoPrice.toString()) : null,
        currency: row.currency || 'EUR',
        balanceInOtherCycles: row.balanceInOtherCycles,
        barnBalanceRows: row.barnBalances.map((b) => ({
          barn: cycle.dayOldChicks.map((d) => d.barn).find((d) => d.id === b.barnId),
          amount: b.amount,
          ratio: b.barnAllocationFactor,
          balanceRowId: `${row.deliveryId}_${row.deliveryLineIdentifier}`,
          productType: row.productType,
        })),
        isUserDefinedRatio: false,
        remainder,
        barnId: row.barnId,
        price: this.calculateBalancePrice(row.nettoAmount, row.baseQuantity, quantity, remainder),
        baseQuantity: row.baseQuantity,
        nettoAmount: row.nettoAmount,
        externalInvoiceId: row.externalInvoiceId,
        invoiceDate: row.invoiceDate,
        externalDeliveryId: row.externalDeliveryId,
      });
    };

    if (!!balanceRows && balanceRows.length > 0) {
      balanceRows.forEach((row) => processRow(row));
      balance.sort(
        fieldSorter([
          { name: 'type', order: OrderType.Ascending },
          { name: 'deliveryDate', order: OrderType.Descending },
        ]),
      );
    }
    return balance;
  }

  calculateBalancePrice(
    nettoAmount: number,
    baseQuantity: number,
    quantity: number,
    remainder: number,
  ): number {
    const cycleConsumption = quantity - remainder;
    return this.calculateBalancePriceForTotalConsumption(
      nettoAmount,
      baseQuantity,
      cycleConsumption,
    );
  }

  calculateBalancePriceForTotalConsumption(
    nettoAmount: number,
    baseQuantity: number,
    barnsTotalConsumption: number,
  ): number {
    if (baseQuantity === 0 || baseQuantity === null || isNaN(baseQuantity)) {
      return 0;
    }

    if (nettoAmount === 0 || nettoAmount === null || isNaN(nettoAmount)) {
      return 0;
    }

    const consumptionPercentage = barnsTotalConsumption / baseQuantity;

    const price = nettoAmount * consumptionPercentage;

    return price;
  }

  processRemaindersInBalanceRows(
    tableData: BalanceTableRow[],
    existingBalancesPerDeliveryLine: BalancePerDeliveryLine[],
    cycle: Cycle,
  ): BalancePerDeliveryLine[] {
    return tableData.map((tableRow) => {
      const existingBalancePerDeliveryLine: BalancePerDeliveryLine =
        existingBalancesPerDeliveryLine.find(
          (d) =>
            d.deliveryId === tableRow.deliveryId &&
            d.deliveryLineIdentifier === tableRow.deliveryLineIdentifier &&
            (tableRow.deliverySource === DeliveryOriginEnum.START ||
              d.deliverySource === tableRow.deliverySource),
        );

      if (existingBalancePerDeliveryLine !== undefined) {
        return {
          ...existingBalancePerDeliveryLine,
          barnBalances: this.calculateBarnBalanceRows(tableRow),
        };
      } else {
        return {
          balanceInOtherCycles: tableRow.balanceInOtherCycles,
          barnBalances: this.calculateBarnBalanceRows(tableRow),
          currency: tableRow.currency,
          customerId: tableRow.customerId,
          cycleId: tableRow.cycleId,
          cycleNumber: cycle.cycleNumber,
          deliveryDate: tableRow.deliveryDate,
          deliveryId: tableRow.deliveryId,
          deliveryLineId: tableRow.deliveryLineId,
          deliveryLineIdentifier: tableRow.deliveryLineIdentifier,
          deliverySource: tableRow.deliverySource,
          locationId: tableRow.locationId,
          productIdentifier: tableRow.productIdentifier,
          productTitle: tableRow.productTitle,
          productType: tableRow.productType,
          unitsDelivered: tableRow.unitsDelivered,
          unitsRemaining: tableRow.quantity,
          unitNettoPrice: tableRow.unitNettoPrice,
          kgDelivered: tableRow.kgDelivered,
          barnId: tableRow.barnId,
          baseQuantity: tableRow.baseQuantity,
          nettoAmount: tableRow.nettoAmount,
        };
      }
    });
  }

  private calculateBarnBalanceRows(tableRow: BalanceTableRow): BarnBalance[] {
    const totalToAllocate = tableRow.quantity - tableRow.remainder;
    let currentlyAllocated = 0;
    const updatedBarnBalanceRows: BarnBalance[] = tableRow.barnBalanceRows.map((row) => {
      const toAllocate = Math.floor(totalToAllocate * row.ratio);
      currentlyAllocated += toAllocate;
      return <BarnBalance>{
        amount: toAllocate,
        barnId: row.barn?.id,
        barnAllocationFactor: row.ratio,
      };
    });

    updatedBarnBalanceRows[updatedBarnBalanceRows.length - 1].amount +=
      totalToAllocate - currentlyAllocated;

    return updatedBarnBalanceRows;
  }

  /**
   * returns week information as '{weeknr} ({startdate} - {enddate})', e.g. '1 (1 jan. 21 - 7 jan 21)'
   */
  getWeekInfo(
    cycleDate: string,
    cycleEndDate: string,
    week: number,
    startDay: number,
    endDay: number,
  ): string {
    const startDate: Date = Utils.toUtcDate(new Date(cycleDate));
    let endDate: Date = Utils.toUtcDate(new Date(cycleDate));
    startDate.setDate(startDate.getDate() + startDay);
    endDate.setDate(endDate.getDate() + endDay);
    if (!!cycleEndDate && endDate.getTime() > new Date(cycleEndDate).getTime()) {
      endDate = Utils.toUtcDate(new Date(cycleEndDate));
    }
    return `${week} (${formatDate(startDate, 'd MMM yy', this.locale.getLocale())} - ${formatDate(
      endDate,
      'd MMM yy',
      this.locale.getLocale(),
    )})`;
  }

  convertObservationsRegistrationToWeekRegistrations(
    observations: ObservationsRegistration[],
    barns: Barn[],
  ): WeekRegistration[] {
    const weekRegistrations: WeekRegistration[] = [];

    for (const barn of barns) {
      let weeks = observations
        .filter(
          (observations) =>
            observations.intervalType === IntervalType.Week && observations.barnId === barn.id,
        )
        .sort((o1, o2) => o1.intervalValue - o2.intervalValue);

      const days = observations
        .filter(
          (observations) =>
            observations.intervalType === IntervalType.Day && observations.barnId === barn.id,
        )
        .sort((o1, o2) => o1.intervalValue - o2.intervalValue);

      if (weeks.length === 0 && days.length > 0) {
        weeks = this.createEmptyObservationRegistrations(days[0].cycleId, days[0].barnId, days);
      }

      for (const week of weeks) {
        const weekRegistration = {
          dayRegistrations: [],
          endDay: week.intervalValue * 7 - 1,
          startDay: week.intervalValue * 7 - 7,
          weekNr: week.intervalValue,
          registrationData: {
            mortality: week.mortality,
            selection: week.selection,
            selectionSmall: week.selectionSmall,
            selectionLegProblems: week.selectionLegProblems,
            feedIntakeInKg: week.feedIntakeInKg,
            waterIntakeInL: week.waterIntakeInL,
            indoorTemperatureInCelsius: week.indoorTemperatureInCelsius,
            outdoorTemperatureInCelsius: week.outdoorTemperatureInCelsius,
            indoorHumidityInPercentages: week.indoorHumidityInPercentages,
            outdoorHumidityInPercentages: week.outdoorHumidityInPercentages,
            chickWeightInGrams: week.chickWeightInGrams,
            litterQualityLoosePercentage: week.litterQualityLoosePercentage,
            litterQualityCakedPercentage: week.litterQualityCakedPercentage,
            litterQualityWetPercentage: week.litterQualityWetPercentage,
          },
          barnId: week.barnId,
        } as WeekRegistration;

        weekRegistrations.push(weekRegistration);

        const dayRegistrations: DayRegistration[] = [];
        for (let i = 0; i < 7; i++) {
          const dayIndex = days.findIndex(
            (day) => day.intervalValue === weekRegistration.startDay + i,
          );

          if (dayIndex > -1) {
            dayRegistrations.push({
              day: weekRegistration.startDay + i,
              registrationData: {
                mortality: days[dayIndex].mortality,
                selection: days[dayIndex].selection,
                selectionSmall: days[dayIndex].selectionSmall,
                selectionLegProblems: days[dayIndex].selectionLegProblems,
                feedIntakeInKg: days[dayIndex].feedIntakeInKg,
                waterIntakeInL: days[dayIndex].waterIntakeInL,
                indoorTemperatureInCelsius: days[dayIndex].indoorTemperatureInCelsius,
                outdoorTemperatureInCelsius: days[dayIndex].outdoorTemperatureInCelsius,
                indoorHumidityInPercentages: days[dayIndex].indoorHumidityInPercentages,
                outdoorHumidityInPercentages: days[dayIndex].outdoorHumidityInPercentages,
                chickWeightInGrams: days[dayIndex].chickWeightInGrams,
                litterQualityLoosePercentage: days[dayIndex].litterQualityLoosePercentage,
                litterQualityCakedPercentage: days[dayIndex].litterQualityCakedPercentage,
                litterQualityWetPercentage: days[dayIndex].litterQualityWetPercentage,
              },
            });
          } else {
            dayRegistrations.push({
              day: weekRegistration.startDay + i,
              registrationData: {
                mortality: null,
                selection: null,
                selectionSmall: null,
                selectionLegProblems: null,
                feedIntakeInKg: null,
                waterIntakeInL: null,
                indoorTemperatureInCelsius: null,
                outdoorTemperatureInCelsius: null,
                indoorHumidityInPercentages: null,
                outdoorHumidityInPercentages: null,
                chickWeightInGrams: null,
                litterQualityLoosePercentage: null,
                litterQualityCakedPercentage: null,
                litterQualityWetPercentage: null,
              },
            });
          }
        }
        weekRegistration.dayRegistrations = dayRegistrations;
      }
    }

    return weekRegistrations;
  }

  createEmptyObservationRegistrations(
    cycleId: number,
    barnId: number,
    observationsRegistrations: ObservationsRegistration[],
  ): ObservationsRegistration[] {
    const weekObservationsRegistrations = [];
    for (
      let i = 0;
      i < (Math.max(...observationsRegistrations.map((or) => or.intervalValue)) + 7) / 7;
      i++
    ) {
      weekObservationsRegistrations.push({
        cycleId,
        barnId,
        intervalType: IntervalType.Week,
        intervalValue: i + 1,
      } as ObservationsRegistration);
    }
    return weekObservationsRegistrations;
  }

  convertWeekRegistrationsToObservationsRegistration(
    changedRegistrations: WeekRegistration[],
    cycleId: number,
  ): ObservationsRegistration[] {
    const observationsRegistrations: ObservationsRegistration[] = [];

    for (const weekRegistration of changedRegistrations) {
      observationsRegistrations.push({
        cycleId,
        barnId: weekRegistration.barnId,
        intervalType: IntervalType.Week,
        intervalValue: weekRegistration.weekNr,
        mortality: weekRegistration.registrationData.mortality,
        selection: weekRegistration.registrationData.selection,
        selectionSmall: weekRegistration.registrationData.selectionSmall,
        selectionLegProblems: weekRegistration.registrationData.selectionLegProblems,
        feedIntakeInKg: weekRegistration.registrationData.feedIntakeInKg,
        waterIntakeInL: weekRegistration.registrationData.waterIntakeInL,
        indoorTemperatureInCelsius: weekRegistration.registrationData.indoorTemperatureInCelsius,
        outdoorTemperatureInCelsius: weekRegistration.registrationData.outdoorTemperatureInCelsius,
        indoorHumidityInPercentages: weekRegistration.registrationData.indoorHumidityInPercentages,
        outdoorHumidityInPercentages:
          weekRegistration.registrationData.outdoorHumidityInPercentages,
        chickWeightInGrams: weekRegistration.registrationData.chickWeightInGrams,
        litterQualityLoosePercentage:
          weekRegistration.registrationData.litterQualityLoosePercentage,
        litterQualityCakedPercentage:
          weekRegistration.registrationData.litterQualityCakedPercentage,
        litterQualityWetPercentage: weekRegistration.registrationData.litterQualityWetPercentage,
      } as ObservationsRegistration);

      for (const dayRegistration of weekRegistration.dayRegistrations) {
        observationsRegistrations.push({
          cycleId,
          barnId: weekRegistration.barnId,
          intervalType: IntervalType.Day,
          intervalValue: dayRegistration.day,
          mortality: dayRegistration.registrationData.mortality,
          selection: dayRegistration.registrationData.selection,
          selectionSmall: dayRegistration.registrationData.selectionSmall,
          selectionLegProblems: dayRegistration.registrationData.selectionLegProblems,
          feedIntakeInKg: dayRegistration.registrationData.feedIntakeInKg,
          waterIntakeInL: dayRegistration.registrationData.waterIntakeInL,
          indoorTemperatureInCelsius: dayRegistration.registrationData.indoorTemperatureInCelsius,
          outdoorTemperatureInCelsius: dayRegistration.registrationData.outdoorTemperatureInCelsius,
          indoorHumidityInPercentages: dayRegistration.registrationData.indoorHumidityInPercentages,
          outdoorHumidityInPercentages:
            dayRegistration.registrationData.outdoorHumidityInPercentages,
          chickWeightInGrams: dayRegistration.registrationData.chickWeightInGrams,
          litterQualityLoosePercentage:
            dayRegistration.registrationData.litterQualityLoosePercentage,
          litterQualityCakedPercentage:
            dayRegistration.registrationData.litterQualityCakedPercentage,
          litterQualityWetPercentage: dayRegistration.registrationData.litterQualityWetPercentage,
        } as ObservationsRegistration);
      }
    }
    return observationsRegistrations;
  }

  getChickBirthDateInSection(cycle: Cycle, barnId: number): Date {
    const dayOldChicksInBarn = cycle.dayOldChicks.find(
      (dayOldChicks) => dayOldChicks.barn.id === barnId,
    );

    if (dayOldChicksInBarn) {
      return DateHelper.fromString(dayOldChicksInBarn.chickBirthDate);
    }

    return undefined;
  }

  getDateOfDayInCycle(cycle: Cycle, dayNumber: number): Date {
    const cycleStartDate = DateHelper.fromString(cycle.cycleDate);
    if (cycleStartDate) {
      return Utils.toUtcDate(
        new Date(cycleStartDate.getTime() + dayNumber * DateHelper.oneDayTime),
      );
    }

    return undefined;
  }
}
